// NOTE: This file is stable and usually should not be modified.
// It is important that all functionality in this file is preserved, and should only be modified if explicitly requested.

import React, { useState, useEffect } from 'react';
import { Sparkles, Key } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogDescription } from "@/components/ui/dialog";
import { toast } from '@/hooks/useToast';
import { useLoginActions } from '@/hooks/useLoginActions';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useTranslation } from '@/hooks/useTranslation';
import { generateSecretKey, nip19, getPublicKey } from 'nostr-tools';
import { genUserName } from '@/lib/genUserName';
import { cn } from '@/lib/utils';

interface SignupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

const SignupDialog: React.FC<SignupDialogProps> = ({ isOpen, onClose, onComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const login = useLoginActions();
  const { t } = useTranslation();
  const { mutateAsync: publishEvent } = useNostrPublish();

  // Generate key and login immediately without showing loading state
  const handleSignup = async () => {
    try {
      // Generate a new secret key
      const sk = generateSecretKey();
      const nsec = nip19.nsecEncode(sk);
      const pubkey = getPublicKey(sk);

      // Store the nsec in localStorage for backup purposes
      localStorage.setItem(`nsec_${pubkey}`, nsec);

      // Login immediately
      login.nsec(nsec);

      // Wait a moment for login to complete
      await new Promise(resolve => setTimeout(resolve, 300));

      // Create a default profile with randomized name
      try {
        await publishEvent({
          kind: 0,
          content: JSON.stringify({
            name: genUserName(pubkey),
          }),
        });
      } catch (profileError) {
        console.error('Failed to create profile:', profileError);
        // Continue anyway - profile can be edited later
      }

      toast({
        title: 'Account Created!',
        description: 'Your secret key has been generated and saved. You can find it in your account settings for backup.',
      });

      // Close and complete
      onClose();
      if (onComplete) {
        setTimeout(() => {
          onComplete();
        }, 100);
      }
    } catch (error) {
      console.error('Signup failed:', error);
      toast({
        title: 'Signup Failed',
        description: 'Failed to create account. Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn("max-w-[95vw] sm:max-w-md max-h-[90vh] max-h-[90dvh] p-0 overflow-hidden rounded-2xl glass-strong overflow-y-scroll border-white/10")}
      >
        <DialogHeader className={cn('px-6 pt-6 pb-1 relative')}>
          <DialogDescription className="text-center text-white/70">
            {t.auth.signup}
          </DialogDescription>
        </DialogHeader>
        <div className='px-6 pt-2 pb-6 space-y-4 relative'>
          <div className='relative z-10 text-center space-y-3'>
            <div className='flex justify-center items-center mb-1'>
              <h1 className='text-4xl font-bold text-white'>NodeCast</h1>
            </div>
            <div className='space-y-2'>
              <p className='text-sm text-white/70'>
                {t.auth.signupDescription}
              </p>
            </div>
          </div>

          {/* Sign Up Button */}
          <div className='flex justify-center'>
            <Button
              className='rounded-full py-6 px-8'
              onClick={handleSignup}
              disabled={isLoading}
            >
              {t.auth.instantSignup}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SignupDialog;
